import React, { useState, useEffect, useMemo } from 'react';
import { LuExternalLink, LuUsers, LuUserSearch } from 'react-icons/lu';
import { API_BASE_URL, CONNECT_UI_URL } from '../../config.js';
import { Card, CardHeader } from '../ui/Card.jsx';
import { Table, THead, SortTh, TBody, Tr, Td } from '../ui/Table.jsx';
import { SearchInput } from '../ui/SearchInput.jsx';
import Alert from '../ui/Alert.jsx';
import Avatar from '../ui/Avatar.jsx';
import Badge from '../ui/Badge.jsx';
import Button from '../ui/Button.jsx';
import EmptyState from '../ui/EmptyState.jsx';
import Skeleton from '../ui/Skeleton.jsx';

const REPORTS_SHOWN = 4;

// Authorization level, for sorting: super admin > admin > standard.
const authLevel = (user) => (user.issuperadmin ? 3 : user.isadmin ? 2 : 1);

const RoleBadge = ({ user }) => {
  if (user.issuperadmin) return <Badge tone="violet" size="sm" dot>Super admin</Badge>;
  if (user.isadmin) return <Badge tone="blue" size="sm" dot>Admin</Badge>;
  return <Badge tone="gray" size="sm">Standard</Badge>;
};

/** Read-only list of the accounts and their reports: Connect is the only writer. */
const UsersManagement = ({ fetchWithAuth }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [configs, setConfigs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const usersResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/users`);
        if (!usersResponse.ok) throw new Error('Failed to fetch users');
        const usersData = await usersResponse.json();

        const configsResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/configs`);
        if (!configsResponse.ok) throw new Error('Failed to fetch configs');
        const configsData = await configsResponse.json();

        setUsers(usersData);
        setConfigs(configsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Impossible de charger les utilisateurs. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchWithAuth]);

  const sortData = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const getUserReportNames = (user) => {
    const allowed = user?.weekly_reports?.allowed;
    if (!Array.isArray(allowed) || allowed.length === 0) return [];
    return allowed.map((id) => configs.find((c) => c.id === id)?.name || `ID : ${id}`);
  };

  const rows = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? users.filter((user) => user.username.toLowerCase().includes(query) || (user.client && user.client.toLowerCase().includes(query)))
      : users;

    if (!sortConfig.key) return filtered;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const compare = {
      issuperadmin: (a, b) => authLevel(a) - authLevel(b),
      client: (a, b) => (a.client || '').localeCompare(b.client || ''),
      reports: (a, b) => {
        const countA = a.weekly_reports?.allowed?.length || 0;
        const countB = b.weekly_reports?.allowed?.length || 0;
        if (countA !== countB) return countA - countB;
        const firstA = (getUserReportNames(a)[0] || '').toLowerCase();
        const firstB = (getUserReportNames(b)[0] || '').toLowerCase();
        return firstA.localeCompare(firstB);
      },
    }[sortConfig.key] || ((a, b) => String(a[sortConfig.key] ?? '').localeCompare(String(b[sortConfig.key] ?? '')));
    return [...filtered].sort((a, b) => compare(a, b) * dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, configs, searchQuery, sortConfig]);

  return (
    <Card>
      <CardHeader
        icon={LuUsers}
        title="Utilisateurs"
        badge={!loading && <Badge tone="violet" size="sm">{users.length}</Badge>}
        subtitle="Comptes ayant accès à Globe Weekly et rapports autorisés"
        actions={(
          <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Rechercher un utilisateur…" className="w-full sm:w-64" />
        )}
      />

      <div className="px-4 pt-4 sm:px-5">
        <Alert
          tone="info"
          title="Les utilisateurs et leurs droits sont gérés dans Globe Connect."
          action={(
            <Button as="a" size="sm" href={`${CONNECT_UI_URL}/admin`} target="_blank" rel="noopener noreferrer" iconRight={LuExternalLink}>
              Ouvrir Connect
            </Button>
          )}
        >
          Cette page est en lecture seule : une modification faite ici n'aurait aucun effet, Connect est le seul écrivain.
        </Alert>
      </div>

      <div className="pt-4">
        {loading ? (
          <div className="space-y-2 px-4 pb-5 sm:px-5">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-11" />)}
          </div>
        ) : error ? (
          <div className="px-4 pb-5 sm:px-5"><Alert tone="danger">{error}</Alert></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={LuUserSearch} tone="gray" title="Aucun utilisateur trouvé" description="Essayez une autre recherche." />
        ) : (
          <Table>
            <THead>
              <SortTh column="username" sort={sortConfig} onSort={sortData}>Nom d'utilisateur</SortTh>
              <SortTh column="client" sort={sortConfig} onSort={sortData}>Client</SortTh>
              <SortTh column="issuperadmin" sort={sortConfig} onSort={sortData}>Autorisation</SortTh>
              <SortTh column="reports" sort={sortConfig} onSort={sortData}>Rapports autorisés</SortTh>
            </THead>
            <TBody>
              {rows.map((user) => {
                const names = getUserReportNames(user);
                return (
                  <Tr key={user.id}>
                    <Td className="whitespace-nowrap">
                      <span className="flex items-center gap-2.5">
                        <Avatar text={user.username} size="sm" round />
                        <span className="font-medium text-gray-900 dark:text-white">{user.username}</span>
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">{user.client || <span className="text-gray-400">—</span>}</Td>
                    <Td><RoleBadge user={user} /></Td>
                    <Td>
                      {names.length > 0 ? (
                        <span className="flex flex-wrap gap-1" title={names.join(', ')}>
                          {names.slice(0, REPORTS_SHOWN).map((name, index) => <Badge key={index} tone="slate" size="sm">{name}</Badge>)}
                          {names.length > REPORTS_SHOWN && <Badge tone="gray" size="sm">+{names.length - REPORTS_SHOWN}</Badge>}
                        </span>
                      ) : (
                        <span className="text-xs italic text-gray-400 dark:text-gray-500">Aucun rapport</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </div>
    </Card>
  );
};

export default UsersManagement;
